import el from "@cypherpotato/el";
import { api } from "../api";
import { Button, PrimaryButton } from "./button";
import { CommandBar } from "./command-bar";
import { Checkbox, FormControl, TextInput } from "./form";
import { Icon } from "./icon";
import { Section } from "./layout";
import { DefaultRow, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "./table";
import { Heading, InlineCode, Paragraph } from "./text";
import { formatDateTime, formatNumber } from "../ui/format";
import { runOperation } from "../ui/operation";
import { find } from "../util";

const reportMonthFormatter = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" });
const reportDateFormatter = new Intl.DateTimeFormat("en", { dateStyle: "medium" });
const usdCurrencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const brlCurrencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const percentFormatter = new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function MonthlyReport(endpoint) {

    const monthlyReportData = el.state.create(null);
    const now = new Date();
    const defaultReportMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    function CostCells(report, cost) {
        return [
            TableCell(usdCurrencyFormatter.format(cost), { title: cost }),
            report.usdBrlRate != null && TableCell(brlCurrencyFormatter.format(cost * report.usdBrlRate))
        ];
    }

    function CostHeaders(report, label) {
        return [
            TableHeaderCell(`${label} (USD)`),
            report.usdBrlRate != null && TableHeaderCell(`${label} (BRL)`)
        ];
    }

    function ReportTable(kind, title, description, headers, rows) {
        return el("section.monthly-report-table-section",
            { classList: [`monthly-report-${kind}`] },
            el("h3", title),
            el("p.monthly-report-section-description", description),
            Table(
                { classList: ["monthly-report-table"] },
                TableHeader(...headers),
                TableBody(...(rows.length > 0 ? rows : [DefaultRow("No usage recorded in this period.")]))
            )
        );
    }

    return el.fragment(
        Section(
            { classList: ["monthly-report-controls"] },
            Heading(
                "Monthly usage report",
                Paragraph("Choose a billing month to generate a print-ready account usage report.")
            ),
            CommandBar(
                FormControl(
                    "Reporting month",
                    TextInput("reportMonth", null, {
                        type: "month",
                        value: defaultReportMonth,
                        max: defaultReportMonth,
                        required: true,
                        ariaLabel: "Reporting month"
                    })
                ),
                FormControl(
                    "Currency",
                    Checkbox("reportIncludeBrl", "Include BRL values")
                ),
                PrimaryButton(
                    Icon("file-chart-line"),
                    "Generate report",
                    {
                        onClick() {
                            runOperation("Generating monthly report...", async () => {
                                const query = new URLSearchParams({
                                    month: find("reportMonth").value,
                                    includeBrl: document.querySelector("input[name=reportIncludeBrl]").checked
                                });
                                const result = await api.getJson(`${endpoint}?${query}`);
                                monthlyReportData.value = result.data;
                            });
                        }
                    }
                ),
                el.state.listening([monthlyReportData], () =>
                    Button(
                        Icon("printer-line"),
                        "Print / Save PDF",
                        {
                            disabled: !monthlyReportData.value,
                            onClick() {
                                const previousTitle = document.title;
                                const reportMonth = reportMonthFormatter.format(new Date(monthlyReportData.value.periodStart));
                                document.title = `AIVAX Monthly Report - ${monthlyReportData.value.accountName} - ${reportMonth}`;
                                document.body.classList.add("printing-monthly-report");
                                window.addEventListener("afterprint", () => {
                                    document.body.classList.remove("printing-monthly-report");
                                    document.title = previousTitle;
                                }, { once: true });
                                window.print();
                            }
                        }
                    )
                )
            )
        ),
        Section(
            { classList: ["monthly-report-surface"] },
            el.state.listening([monthlyReportData], () => {
                const report = monthlyReportData.value;
                if (!report) {
                    return el("div.monthly-report-empty");
                }

                const periodStart = new Date(report.periodStart);

                return el("article.monthly-report",
                    el("header.monthly-report-header",
                        el("div",
                            el("div.monthly-report-brand", "AIVAX"),
                            el("h2", "Monthly Usage Report"),
                            el("p.monthly-report-month", reportMonthFormatter.format(periodStart))
                        ),
                        el("dl.monthly-report-period",
                            el("div", el("dt", "Account"), el("dd", report.accountName)),
                            el("div", el("dt", "Period"), el("dd", `${reportDateFormatter.format(periodStart)} – ${reportDateFormatter.format(new Date(report.periodEnd))}`)),
                            el("div", el("dt", "Generated"), el("dd", formatDateTime(report.generatedAt))),
                            el("div", el("dt", "Token volume"), el("dd", `${formatNumber(report.tokenVolume)} tokens`)),
                            report.usdBrlRate != null && el("div", el("dt", "Exchange rate"), el("dd", `USD 1.00 = ${brlCurrencyFormatter.format(report.usdBrlRate)}`))
                        )
                    ),
                    ReportTable(
                        "statement",
                        "Statement",
                        "Amounts billed in the period, grouped by charge type.",
                        [TableHeaderCell("Charge"), ...CostHeaders(report, "Amount")],
                        [
                            ["Inference", "Text generation with language models: chat completions, tool handling, structured functions and batch processing.", report.inferenceCost],
                            ["RAG", "Retrieval-augmented generation: document and query embeddings, segmentation, retrieval answers and reranking.", report.ragCost],
                            ["Services", "Platform services such as web search, OCR, media description, speech, image generation, decisions and storage.", report.serviceCost]
                        ].map(([title, description, cost]) =>
                            TableRow(
                                TableCell(el("strong", title), el("span.monthly-report-line-description", description)),
                                ...CostCells(report, cost)
                            )
                        ).concat(
                            TableRow(
                                { classList: ["monthly-report-total"] },
                                TableCell(el("strong", "Total billed")),
                                ...CostCells(report, report.totalCost)
                            )
                        )
                    ),
                    ReportTable(
                        "models",
                        "Models used",
                        "Models ranked by billed cost. Input tokens include cached tokens; cached share is the cached part of the input.",
                        [
                            TableHeaderCell("Model"),
                            TableHeaderCell("Input tokens"),
                            TableHeaderCell("Cached tokens"),
                            TableHeaderCell("Cached %"),
                            TableHeaderCell("Output tokens"),
                            ...CostHeaders(report, "Cost")
                        ],
                        report.models.map(item =>
                            TableRow(
                                TableCell(item.name === "Other" ? el("strong", item.name) : InlineCode(item.name)),
                                TableCell(formatNumber(item.inputTokens)),
                                TableCell(formatNumber(item.cachedTokens)),
                                TableCell(item.inputTokens > 0 ? percentFormatter.format(item.cachedTokens / item.inputTokens) : "—"),
                                TableCell(formatNumber(item.outputTokens)),
                                ...CostCells(report, item.cost)
                            )
                        )
                    ),
                    ReportTable(
                        "resources",
                        "Resources used",
                        "Resources ranked by attributed cost.",
                        [TableHeaderCell("Resource"), TableHeaderCell("Type"), ...CostHeaders(report, "Cost")],
                        report.resources.map(item =>
                            TableRow(
                                TableCell(item.name === "Other" ? el("strong", item.name) : item.name),
                                TableCell(item.type),
                                ...CostCells(report, item.cost)
                            )
                        )
                    ),
                    ReportTable(
                        "skus",
                        "SKUs used",
                        "SKUs ranked by billed cost, with the billed quantity in each SKU's unit.",
                        [TableHeaderCell("SKU"), TableHeaderCell("Quantity"), ...CostHeaders(report, "Cost")],
                        report.skus.map(item =>
                            TableRow(
                                TableCell(item.name === "Other" ? el("strong", item.name) : InlineCode(item.name)),
                                TableCell(formatNumber(item.quantity), { title: item.quantity }),
                                ...CostCells(report, item.cost)
                            )
                        )
                    ),
                    el("footer.monthly-report-notes",
                        el("h3", "Report notes"),
                        el("ul",
                            el("li", "Each table shows up to 10 items by cost. Other consolidates the remaining items."),
                            el("li", "Resource cost is attributed to every related resource, so one charge may appear in more than one resource total."),
                            el("li", "Token volume includes token-metered inference, batch and RAG SKUs. Request-priced reranking is included in RAG without being added as tokens."),
                            report.usdBrlRate != null && el("li", "BRL values use the exchange rate at the generation time, not the rate of each charge date.")
                        )
                    )
                );
            })
        )
    );
}
